import React, { FC, lazy, memo, Suspense, useEffect, useMemo } from 'react'

import { FocusNode, useFocusNodeById, useSetFocus } from '@please/lrud'

import { getPageById } from '@/actions/actions'
import { Catalogue } from '@/components/Catalogue/Catalogue'
import { ContentUnavailable } from '@/components/ContentUnavailable/ContentUnavailable'
import { DataWidget } from '@/components/DataWidget/DataWidget'
import { HtmlWidget } from '@/components/HtmlWidget/HtmlWidget'
import { Loader } from '@/components/Loader'
import { PageBackground } from '@/components/PageContent/PageBackground'
import { PageFallback } from '@/components/PageFallback/PageFallback'
import { PageHeader } from '@/components/PageHeader/PageHeader'
import { pageTypes } from '@/constants/common'
import { focusIds, orientation } from '@/constants/focus'
import { popupStatus } from '@/constants/popup'
import { scrollOptions } from '@/constants/scrollers'
import { HomeTabPage } from '@/containers/HomeTabPage/HomeTabPage'
import { Container as PageContainer } from '@/containers/styles'
import { useCustomFocus, useFirstRender } from '@/hooks'
import { useFindNearestByImgWidth } from '@/hooks/useFindNearestByImgWidth'
import { usePageViewAnalytics } from '@/hooks/usePageViewAnalytics'
import { useCustomPalette } from '@/hooks/useSetPalette'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import useStore from '@/store/useStore'
import {
  PageContentProps,
  SectionGridGroupProps,
} from '@/types/componentsPropsTypes'
import { LinesGroupType, scrollOptionsType } from '@/types/sliderTypes'

import { ContentUnavailableWrapper, PageContentLoaderWrapper } from './styles'

const SectionGridGroup: FC<SectionGridGroupProps> = lazy(
  () => import('@/components/SectionGridGroup/SectionGridGroup'),
)
const LinesGroup: FC<LinesGroupType> = lazy(
  () => import('@/components/LinesGroup/LinesGroup'),
)

const pageContentHtmlWidgetSize = {
  width: 1584,
  height: 832,
}

const PageContentRaw: FC<PageContentProps> = ({
  isSectionTitleHidden,
  isPopup,
  activeTabIndex,
  renderedTabNav,
  startFocusChild,
}) => {
  const { t } = useCommonTranslation()
  const focusIdPrefix = isPopup ? focusIds.PAGE_POPUP : ''
  const focusId = `${focusIdPrefix}${focusIds.PAGE}${focusIds.CONTAINER_MOD}`

  const setFocus = useSetFocus()
  const isFirstRender = useFirstRender()

  const listPopup = useStore((state) => state.common.listPopup)
  const currentEvent = useStore((state) => state.common.currentEvent)

  const { page: pageData, loading: isPageLoading } = useStore(
    (state) => state.page,
  )
  const { pagePopup: pagePopupData, pagePopupStatus } = useStore(
    (state) => state.popup,
  )
  const { pageImgSrc } = useCustomPalette()

  const focusNode = useFocusNodeById(focusId)

  const { defaultFocusChild, onMove } = useCustomFocus({
    focusId,
    startFocusChild,
  })

  const isPopupLoading = pagePopupStatus === popupStatus.LOADING
  const isPagePopupOpen = pagePopupStatus !== popupStatus.CLOSED

  const isLoading = isPopup ? isPopupLoading : isPageLoading

  const page = isPopup ? pagePopupData : pageData
  const tab = page?.tabs?.[activeTabIndex ?? 0]

  const isTabTemplate = page?.pageType === pageTypes.TABS
  const isLandingPage = page?.pageType === pageTypes.LANDING
  const isHomeTabPage = page?.pageType === pageTypes.HOME_TAB

  const content = isTabTemplate ? tab : page
  const contentType = isTabTemplate ? tab?.type : page?.pageType

  const isPageAvailable =
    !!page?.pageType && (!isTabTemplate || contentType !== pageTypes.HOME_TAB)
  const pageContainerClassName =
    isLandingPage || isHomeTabPage
      ? 'content-wrapper-wihtout-paddings'
      : 'content-wrapper'

  const isContentUnavailable = !isFirstRender && !isPageAvailable && !isLoading
  const hasChildPopup = Boolean(listPopup || currentEvent)
  const isPageContentVisible = (isPopup || !isPagePopupOpen) && !hasChildPopup

  const isPageContentReady =
    (isPageAvailable && !isLoading) || isContentUnavailable

  usePageViewAnalytics(page, isPopup)
  useEffect(() => {
    const shouldSetFocus =
      isPageContentVisible && focusNode && isPageContentReady

    if (shouldSetFocus) {
      setTimeout(setFocus, 0, focusId)
    }
  }, [isPageContentVisible, !!focusNode, isPageContentReady])

  const pageLogoSrc = useFindNearestByImgWidth(page?.logo?.sources, 220)?.url

  const secondLogo = page?.secondLogo?.sources?.[0]?.url

  const scrollOptionsProp = useMemo(() => {
    let options: scrollOptionsType = scrollOptions.PageScroll
    if (contentType === pageTypes.LANDING) {
      options = scrollOptions.LandingPageScroll
    } else if (isTabTemplate) {
      if (contentType === pageTypes.SECTION_GRID) {
        options = scrollOptions.TabScroll.highlights
      }

      if (contentType === pageTypes.LINES) {
        options = scrollOptions.TabScroll.eventsAndClips
      }

      if (contentType === pageTypes.CATALOGUE) {
        options = scrollOptions.TabScroll.catalogue
      }
    } else if (contentType === pageTypes.CATALOGUE) {
      options = scrollOptions.CataloguePageScroll.classicType
    } else if (contentType === pageTypes.HOME_TAB) {
      options = scrollOptions.HomeTabPageScroll
    }

    return {
      ...options,
      containerId: focusIdPrefix + options.containerId,
    }
  }, [focusIdPrefix, scrollOptions, contentType])

  const reloadPage = () => {
    const { id } = page || {}
    if (!id) {
      return
    }
    void getPageById(id)
  }

  return (
    <>
      {pageImgSrc && <PageBackground src={pageImgSrc} />}
      <PageContainer
        focusId={focusId + 'PageContainer'}
        elementType="div"
        orientation={orientation.VERTICAL}
        className={pageContainerClassName}
        defaultFocusChild={1}
      >
        {isPageAvailable && !isLoading && (
          <>
            {isLandingPage || isHomeTabPage || (
              <PageHeader
                page={page}
                logo={pageLogoSrc}
                secondLogo={secondLogo}
                name={page.title || page.shortTitle || page.name}
                isPopup={isPopup}
              />
            )}
            <FocusNode
              focusId={focusId}
              orientation={orientation.VERTICAL}
              defaultFocusChild={defaultFocusChild}
              onMove={onMove}
            >
              {isTabTemplate && renderedTabNav}
              {contentType === pageTypes.GRID && (
                <ContentUnavailableWrapper>
                  <ContentUnavailable>
                    {t('pageContentTypeUnavailable', {
                      pageType: contentType,
                    })}
                  </ContentUnavailable>
                </ContentUnavailableWrapper>
              )}
              {contentType === pageTypes.LANDING && !!page.landing && (
                <SectionGridGroup
                  focusIdPrefix={focusIdPrefix}
                  sections={page.landing?.sectionGrid?.sections || []}
                  landingTop={{
                    backgroundImage: page.landing.backgroundImage,
                    backgroundImagePosition:
                      page.landing.backgroundImagePosition,
                    logo: page.landing.logo,
                    qrCodeUrl: page.landing.qrCodeUrl,
                    qrCodeDescription: page.landing.qrCodeDescription,
                  }}
                  scrollOptions={scrollOptionsProp}
                  isSectionTitleHidden={isSectionTitleHidden}
                  isPopup={isPopup}
                  isLandingPage={isLandingPage}
                  anchorId={page.anchorId}
                  initFocusId={page.focusId}
                />
              )}
              {contentType === pageTypes.LINES && !!content?.lines?.length && (
                <Suspense>
                  <LinesGroup
                    focusIdPrefix={focusIdPrefix}
                    groupData={content.lines}
                    scrollOptions={scrollOptionsProp}
                    isSectionTitleHidden={isSectionTitleHidden}
                    anchorId={page.anchorId}
                    initFocusId={page.focusId}
                    isPopup={isPopup}
                  />
                </Suspense>
              )}
              {contentType === pageTypes.SECTION_GRID &&
                !!content?.sectionGrid?.sections?.length && (
                  <Suspense>
                    <SectionGridGroup
                      focusIdPrefix={focusIdPrefix}
                      sections={content.sectionGrid.sections}
                      scrollOptions={scrollOptionsProp}
                      isSectionTitleHidden={isSectionTitleHidden}
                      isPopup={isPopup}
                      anchorId={page.anchorId}
                      initFocusId={page.focusId}
                    />
                  </Suspense>
                )}
              {contentType === pageTypes.HTML_WIDGET &&
                !!content?.htmlWidget && (
                  <HtmlWidget
                    url={content.htmlWidget.url}
                    htmlWidgetSize={pageContentHtmlWidgetSize}
                  />
                )}
              {contentType === pageTypes.DATA_WIDGET &&
                !!content?.dataWidget && (
                  <DataWidget
                    isPopup={!!isPopup}
                    match={content.match}
                    dataWidget={content.dataWidget}
                    dataExtension={page.dataExtension}
                  />
                )}
              {contentType === pageTypes.CATALOGUE &&
                !!content?.catalogue?.tiles?.length && (
                  <Catalogue
                    catalogue={content.catalogue}
                    focusIdPrefix={focusIdPrefix}
                    initFocusId={page.focusId}
                    anchorId={page.anchorId}
                    isPopup={isPopup}
                    scrollOptions={scrollOptionsProp}
                  />
                )}
              {contentType === pageTypes.HOME_TAB && !!page?.homeTab && (
                <HomeTabPage
                  key={page.id}
                  id={page.id}
                  homeTab={page.homeTab}
                  focusIdPrefix={focusIdPrefix}
                  tabIndex={page.tabIndex}
                  slideIndex={page.slideIndex}
                  anchorId={page.anchorId}
                  initFocusId={page.focusId}
                  isPopup={isPopup}
                  scrollOptions={scrollOptionsProp}
                />
              )}
            </FocusNode>
          </>
        )}
        {(isContentUnavailable || isLoading) && (
          <PageContentLoaderWrapper>
            {isContentUnavailable && (
              <PageFallback reloadHandler={reloadPage} />
            )}
            {isLoading && <Loader />}
          </PageContentLoaderWrapper>
        )}
      </PageContainer>
    </>
  )
}

export const PageContent = memo(PageContentRaw)
